import {Sparkles, TrendingUp, Shield, Ruler} from 'lucide-react'
import Calculator from "@/components/Calculator"
import Header from "@/components/Header"
import Footer from "@/components/Footer"

function Home() {
  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-emerald-50">
      <Header />
      
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-4">
            Professional Gemstone Weight & Value Calculator
          </h1>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto">
            The most accurate gemstone calculator for estimating carat weight and market value for Emeralds, Sapphires, Opals, and more.
          </p>
        </div>

        {/* Calculator */}
        <div className="mb-16">
          <Calculator />
        </div>

        {/* Features */}
        <div className="grid md:grid-cols-4 gap-6 mb-16">
          <div className="bg-white p-6 rounded-xl border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <Sparkles className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Accurate Calculations</h3>
            <p className="text-sm text-gray-600">
              Uses precise specific gravity (SG) measurements for scientifically accurate weight estimations.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
              <TrendingUp className="w-6 h-6 text-blue-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Market Value Estimates</h3>
            <p className="text-sm text-gray-600">
              Get instant price ranges based on current market data and quality grades.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-emerald-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center mb-4">
              <Shield className="w-6 h-6 text-amber-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Avoid Overpaying</h3>
            <p className="text-sm text-gray-600">
              Make informed purchase decisions with reliable worth and appraisal estimates.
            </p>
          </div>

          <div className="bg-white p-6 rounded-xl border border-blue-100 shadow-sm hover:shadow-md transition-shadow">
            <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center mb-4">
              <Ruler className="w-6 h-6 text-emerald-600" />
            </div>
            <h3 className="font-bold text-gray-900 mb-2">Multiple Shapes</h3>
            <p className="text-sm text-gray-600">
              Supports all popular gemstone cuts including round, oval, emerald, pear, and more.
            </p>
          </div>
        </div>

        {/* How It Works */}
        <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-8 mb-16">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
            How to Measure Your Gemstone
          </h2>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-blue-700 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                1
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Measure Dimensions</h3>
              <p className="text-gray-600 text-sm">
                Use a digital caliper or ruler to measure the length, width, and depth of your gemstone in millimeters. Accuracy is key for precise calculations.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-blue-700 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                2
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Select Gemstone Type</h3>
              <p className="text-gray-600 text-sm">
                Choose your gemstone type from the dropdown. Each stone has a unique specific gravity that affects weight calculations.
              </p>
            </div>

            <div>
              <div className="w-12 h-12 bg-gradient-to-br from-emerald-600 to-blue-700 text-white rounded-full flex items-center justify-center font-bold text-xl mb-4">
                3
              </div>
              <h3 className="font-bold text-gray-900 mb-2">Choose Grade & Get Results</h3>
              <p className="text-gray-600 text-sm">
                Select Commercial, Fine, or Extra Fine quality. Weight is volume × shape factor × specific gravity; value uses your grade. Copy results for your records.
              </p>
            </div>
          </div>
        </div>

        {/* About Section */}
        <div className="bg-gradient-to-r from-emerald-600 to-blue-700 rounded-2xl p-8 md:p-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Understanding Specific Gravity in Gemstone Calculations</h2>
          <p className="text-emerald-50 mb-6 text-lg">
            Our gemstone calculator uses the scientific principle of specific gravity (SG) to provide accurate carat weight estimations. Specific gravity is the ratio of a gemstone's density compared to water, and each gemstone type has its unique SG value.
          </p>
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="font-bold text-xl mb-3">Why Specific Gravity Matters</h3>
              <p className="text-emerald-50 text-sm">
                Two gemstones with identical dimensions can have vastly different weights. For example, a 10mm sapphire (SG: 4.0) weighs significantly more than a 10mm emerald (SG: 2.72). Our calculator accounts for these differences to give you precise estimates.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm rounded-lg p-6">
              <h3 className="font-bold text-xl mb-3">Professional-Grade Accuracy</h3>
              <p className="text-emerald-50 text-sm">
                Used by jewelers and gemologists worldwide, this tool helps you determine the approximate price, value, worth, and appraisal range of your gemstones before making purchase or sale decisions.
              </p>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  )
}

export default Home
